import React from 'react';

import {
  SidePage,
  LegacyText,
  Grid,
  Section,
  Typography,
  Stack,
  makeStyles,
} from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import { useNumberFormat } from '@gogame/intl-ts';

import { AffectedEntitiesSection } from 'scenes/SmartRules/components/AffectedEntities';
import { Conditions } from 'scenes/SmartRules/components/Conditions';
import { DynamicAdvertisingTargetDetails } from 'scenes/SmartRules/components/DynamicAdvertisingTargetDetails';
import { RunResults } from 'scenes/SmartRules/components/RunResults';
import { useSmartRuleResults, useIsUpdating } from 'scenes/SmartRules/data';
import { useInspection } from 'scenes/SmartRules/hooks';

import Metadata from '../SmartRuleRunResults/Metadata';
import { Actions } from '../components/Actions';
import { ACTION_APPROVE, ACTION_EDIT, ACTION_REJECT } from '../constants';

const useStyles = makeStyles({
  container: {
    overflowX: 'auto',
    maxHeight: 'calc(100vh - 350px)',
    maxWidth: '705px',
  },
});

export const SmartRuleStartPreview = ({ id, onClose, onExited, open }) => {
  const [childComponent, openChildComponent] = useChildComponent();

  const { data, isLoading } = useSmartRuleResults({ id });

  const classes = useStyles();
  const isUpdating = useIsUpdating();
  const numberFormatter = useNumberFormat();
  const inspection = useInspection({ data });

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading || isUpdating}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={'Smart Rule Start Preview'}
        subTitle={data?.name}
        entityId={id}
      />
      <SidePage.NewBody wrapper={true} dense={true}>
        <SidePage.NewContent withSidebar={true}>
          {data ? (
            <React.Fragment>
              <Section title={'Results'}>
                <RunResults data={data} />
              </Section>
              {data.conditions?.length ? (
                <Section
                  title={`Conditions (${data.conditions.length})`}
                  isCollapsable={true}
                  initialOpen={false}
                >
                  <Grid item container xs={12} spacing={2}>
                    <Grid item container xs={12} spacing={2}>
                      <Grid item xs={12}>
                        <Conditions conditions={data.conditions} />
                      </Grid>
                    </Grid>
                    {data.dynamicTarget ? (
                      <Grid item container xs={12} spacing={2}>
                        <Grid item xs={12}>
                          <LegacyText variant={'subtitle1'}>
                            Dynamic Conditions Population
                          </LegacyText>
                        </Grid>
                        <Grid item xs={12}>
                          <DynamicAdvertisingTargetDetails
                            data={data.dynamicTarget}
                          />
                        </Grid>
                      </Grid>
                    ) : null}
                  </Grid>
                </Section>
              ) : null}
              <Section
                title={`Affected Entities (${numberFormatter(
                  data.affectedEntitiesCount
                )})`}
                isCollapsable
              >
                <Stack className={classes.container}>
                  <AffectedEntitiesSection data={data} />
                </Stack>
              </Section>
            </React.Fragment>
          ) : null}
        </SidePage.NewContent>
        <SidePage.Sidebar
          actions={
            inspection ? (
              <Typography>Inspecting</Typography>
            ) : (
              <Actions
                onOpenChildComponent={openChildComponent}
                limit={3}
                data={{
                  ...data,
                  ruleActions: data?.ruleActions.filter(
                    (action) =>
                      action === ACTION_EDIT ||
                      action === ACTION_APPROVE ||
                      action === ACTION_REJECT
                  ),
                }}
              />
            )
          }
        >
          <Metadata data={data} />
        </SidePage.Sidebar>
      </SidePage.NewBody>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};
